import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RecurrenceFrequency, TaskExecutionScope } from '~/data/endpoints/scheduled-tasks';

/**
 * Pre-filled draft used when opening the scheduled-task dialog from the
 * command bar. All fields are optional so callers can supply just the
 * scaffolding they want (e.g. only `title` + `prompt` + `recurrence` for
 * preset shortcuts).
 */
export interface ScheduledTaskDraft {
  title?: string;
  description?: string;
  prompt?: string;
  recurrence?: RecurrenceFrequency;
  scope?: TaskExecutionScope;
}

interface CommandBarState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  /** When set, the ScheduledTaskDialogHost opens with the draft pre-filled. */
  scheduledTaskDraft: ScheduledTaskDraft | null;
  openScheduledTaskDialog: (draft?: ScheduledTaskDraft) => void;
  closeScheduledTaskDialog: () => void;

  /** Controls the Add Meeting (.ics / web calendar) dialog. */
  meetingDialogOpen: boolean;
  openMeetingDialog: () => void;
  closeMeetingDialog: () => void;

  /** Controls the keyboard shortcuts cheat-sheet modal. */
  shortcutsDialogOpen: boolean;
  openShortcutsDialog: () => void;
  closeShortcutsDialog: () => void;
}

/**
 * Manages global UI state owned by the command bar:
 *   - the palette open/close state
 *   - any "host" dialogs the palette can summon (scheduled task, meeting,
 *     keyboard-shortcuts cheat-sheet)
 *
 * Centralising these flags here means every action can imperatively trigger
 * UI from anywhere in the tree without prop-drilling or context.
 */
export const useCommandBarStore = create<CommandBarState>()(
  devtools(
    (set) => ({
      isOpen: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),

      scheduledTaskDraft: null,
      openScheduledTaskDialog: (draft) => set({ scheduledTaskDraft: draft ?? {}, isOpen: false }),
      closeScheduledTaskDialog: () => set({ scheduledTaskDraft: null }),

      meetingDialogOpen: false,
      openMeetingDialog: () => set({ meetingDialogOpen: true, isOpen: false }),
      closeMeetingDialog: () => set({ meetingDialogOpen: false }),

      shortcutsDialogOpen: false,
      openShortcutsDialog: () => set({ shortcutsDialogOpen: true, isOpen: false }),
      closeShortcutsDialog: () => set({ shortcutsDialogOpen: false }),
    }),
    { name: 'command-bar-store' },
  ),
);
